'use client';

import { Link, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SVGProps } from 'react';
import { MapCard, type MapMarker } from './MapCard';
import { ParkingTableWithChart } from './ParkingTableWithChart';
import type { ParkingCity, ParkingData, ParkingFootprint, ParkingSpotData, ParkingSpotWithChart } from '../lib/parkingTypes';
import { useAuth } from '../hooks/useAuth';
import { useSelectedCity } from '../hooks/useSelectedCity';
import { callParkingBoxApi } from '../lib/parkingBoxApi';
import { normalizeParkingListResponse, type ParkingListItem } from '../lib/parkingList';
import { fetchLastUpdateInfo, type LastUpdateInfo } from '../lib/lastUpdate';
import { normalizeCityListResponse, type ParkingCityItem } from '../lib/parkingCities';
import { parseParkingAreasResponse, pickFootprintFromAreas } from '../lib/parkingAreas';
import {
  parseParkingSpotTypesResponse,
  readCachedParkingSpotTypes,
  readCachedParkingSpotTypesMany,
  writeCachedParkingSpotTypesArea,
  type ParkingSpotTypeMap,
} from '../lib/parkingSpotTypes';
import { normalizeParkingIdentifier, normalizeParkingUtilizationResponse } from '../lib/parkingUtilization';

type ParkingDashboardProps = {
  parkingData: ParkingData;
};

type ParkingOccupancySummary = {
  total: number | null;
  occupied: number | null;
  free: number | null;
  freeHandi: number | null;
  freeEv: number | null;
};

type ParkingOccupancyEntry = {
  status: 'loading' | 'success' | 'error';
  summary: ParkingOccupancySummary | null;
  error: string | null;
};

const PARKING_LIST_REFRESH_INTERVAL_MS = 30000;

type MobileActiveParkingCardProps = {
  spot: ParkingSpotData | null;
  cityId: string;
};

function MobileActiveParkingCard({ spot, cityId }: MobileActiveParkingCardProps) {
  if (!spot) {
    return (
      <div className="rounded-[26px] border border-dashed border-slate-300 bg-slate-50/70 px-4 py-8 text-center text-sm font-medium text-slate-500 dark:border-white/15 dark:bg-white/[0.04] dark:text-slate-300/80">
        Brak parkingów do wyświetlenia.
      </div>
    );
  }

  const freeSpaces = Math.max(spot.total - spot.occupied, 0);
  const occupancyPercentage = spot.total > 0 ? Math.round((spot.occupied / spot.total) * 100) : 0;
  const displayLabel = spot.label?.trim().length ? spot.label : spot.street;
  const actionHref = `/parking/${encodeURIComponent(cityId)}/${encodeURIComponent(spot.id)}`;

  return (
    <div className="rounded-[26px] border border-emerald-400 bg-emerald-50/90 p-4 text-left shadow-[0_22px_45px_-30px_rgba(16,185,129,0.45)] ring-1 ring-emerald-400 transition-all duration-200 dark:border-emerald-400/70 dark:bg-emerald-500/10 dark:ring-emerald-400/60">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="rounded-full border border-emerald-500 bg-emerald-500 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white dark:border-emerald-400 dark:bg-emerald-400/90 dark:text-slate-950">
            Wybrany parking
          </span>
          <p className="mt-3 text-sm font-semibold uppercase leading-snug tracking-[0.18em] text-emerald-700 dark:text-emerald-300">{displayLabel}</p>
          {spot.street !== displayLabel ? (
            <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/75">{spot.street}</p>
          ) : null}
        </div>
        <Link
          to={actionHref}
          className="shrink-0 rounded-xl border border-emerald-500 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white dark:border-emerald-400 dark:text-emerald-300 dark:hover:bg-emerald-400/20"
        >
          Szczegóły
        </Link>
      </div>

      <div className="mt-4">
        <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
          <div className="h-full rounded-full bg-rose-500 transition-[width] duration-200" style={{ width: `${occupancyPercentage}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-300/75">
          <span>{occupancyPercentage}% zajęte</span>
          <span className="text-emerald-600 dark:text-emerald-300">{freeSpaces} wolnych</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-2xl border border-slate-200 bg-white/85 px-3 py-3 text-center dark:border-white/10 dark:bg-white/[0.05]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300/75">Miejsca</p>
          <p className="mt-2 text-lg font-semibold text-slate-700 dark:text-slate-100">{spot.total}</p>
        </div>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-center dark:border-rose-400/20 dark:bg-rose-500/10">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-rose-500 dark:text-rose-200/85">Zajęte</p>
          <p className="mt-2 text-lg font-semibold text-rose-600 dark:text-rose-200">{spot.occupied}</p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-center dark:border-emerald-400/20 dark:bg-emerald-500/10">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-200/85">Wolne</p>
          <p className="mt-2 text-lg font-semibold text-emerald-600 dark:text-emerald-200">{freeSpaces}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-200">
          Niepełn.: {spot.freeHandi ?? '--'}
        </span>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">
          EV: {spot.freeEv ?? '--'}
        </span>
      </div>
    </div>
  );
}

export function ParkingDashboard({ parkingData }: ParkingDashboardProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isCityListCollapsed, setIsCityListCollapsed] = useState(false);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return window.matchMedia('(min-width: 768px)').matches;
  });
  const settingsRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { logout, token, userEmail, isAdmin } = useAuth();
  const { selectedCityId, setSelectedCityId } = useSelectedCity();
  const [parkingListItems, setParkingListItems] = useState<ParkingListItem[] | null>(null);
  const [parkingListError, setParkingListError] = useState<string | null>(null);
  const [isParkingListLoading, setIsParkingListLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<LastUpdateInfo | null>(null);
  const [remoteCities, setRemoteCities] = useState<ParkingCityItem[]>([]);
  const [remoteFootprints, setRemoteFootprints] = useState<Record<string, ParkingFootprint>>({});
  const [parkingAreaIds, setParkingAreaIds] = useState<Record<string, string[]>>({});
  const [parkingSpotTypes, setParkingSpotTypes] = useState<Record<string, ParkingSpotTypeMap>>({});
  const [parkingUtilizations, setParkingUtilizations] = useState<Record<string, number[]>>({});
  const [parkingOccupancyResponses, setParkingOccupancyResponses] = useState<Record<string, ParkingOccupancyEntry>>({});
  const [loadingUtilizationIds, setLoadingUtilizationIds] = useState<Set<string>>(new Set());
  const fetchingFootprintIdsRef = useRef<Set<string>>(new Set());
  const fetchingSpotTypeIdsRef = useRef<Set<string>>(new Set());
  const fetchedSpotTypeIdsRef = useRef<Set<string>>(new Set());
  const fetchingUtilizationIdsRef = useRef<Set<string>>(new Set());
  const fetchingOccupancyIdsRef = useRef<Set<string>>(new Set());
  const prefetchedSpotTypeParkingIdsRef = useRef<Set<string>>(new Set());
  const parkingListFetchedRef = useRef(false);
  const [activeSpotId, setActiveSpotId] = useState<string | null>(null);
  const displayUserEmail = userEmail ?? 'Brak adresu e-mail';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
    };

    setIsDesktopLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    mediaQuery.addListener(handleChange);
    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  const updateParkingSpotTypesState = useCallback((parkingId: string, nextSpotTypes: ParkingSpotTypeMap) => {
    if (!Object.keys(nextSpotTypes).length) {
      return;
    }

    setParkingSpotTypes((prev) => {
      const previousSpotTypes = prev[parkingId];
      if (areSpotTypeMapsEqual(previousSpotTypes, nextSpotTypes)) {
        return prev;
      }

      return {
        ...prev,
        [parkingId]: nextSpotTypes,
      };
    });
  }, []);

  const handleLogout = useCallback(() => {
    setSettingsOpen(false);

    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!settingsRef.current) {
        return;
      }
      if (!settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }

    let isCancelled = false;
    let isFetchRunning = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const fetchParkingList = async () => {
      if (isFetchRunning || isCancelled) {
        return;
      }
      isFetchRunning = true;
      setIsParkingListLoading(true);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-list',
          },
          {
            jwtToken: token,
            useStoredToken: true,
          }
        );

        if (isCancelled) {
          return;
        }

        const parsedItems = normalizeParkingListResponse(response.json);
        setParkingListItems(parsedItems);
        parkingListFetchedRef.current = true;
        setParkingListError(null);
      } catch (error) {
        if (isCancelled) {
          return;
        }
        const message = error instanceof Error && error.message ? error.message : 'Nie udało się pobrać listy parkingów.';
        setParkingListError(message);
      } finally {
        isFetchRunning = false;
        if (!isCancelled) {
          setIsParkingListLoading(false);
        }
      }
    };

    fetchParkingList();

    intervalId = setInterval(() => {
      void fetchParkingList();
    }, PARKING_LIST_REFRESH_INTERVAL_MS);

    return () => {
      isCancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setRemoteCities([]);
      return;
    }

    let isCancelled = false;

    const fetchCities = async () => {
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-cities',
          },
          {
            jwtToken: token,
            useStoredToken: true,
          }
        );
        const parsed = normalizeCityListResponse(response.json);
        if (!isCancelled) {
          setRemoteCities(parsed);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('[ParkingDashboard] Nie udało się pobrać listy miast:', error);
          setRemoteCities([]);
        }
      }
    };

    fetchCities();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLastUpdate(null);
      return;
    }

    let isCancelled = false;

    const fetchLastUpdate = async () => {
      try {
        const result = await fetchLastUpdateInfo({ jwtToken: token });
        if (!isCancelled) {
          setLastUpdate(result);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('[ParkingDashboard] Nie udało się pobrać daty aktualizacji:', error);
          setLastUpdate(null);
        }
      }
    };

    fetchLastUpdate();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const requestParkingFootprint = useCallback(
    async (parkingId: string | number | null | undefined) => {
      if (!token) {
        return [];
      }
      const resolvedId = normalizeParkingIdentifier(parkingId);
      if (!resolvedId) {
        return [];
      }
      const cachedAreaIds = parkingAreaIds[resolvedId];
      if (remoteFootprints[resolvedId] && cachedAreaIds) {
        return cachedAreaIds;
      }
      const inFlight = fetchingFootprintIdsRef.current;
      if (inFlight.has(resolvedId)) {
        return cachedAreaIds ?? [];
      }
      inFlight.add(resolvedId);
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-area',
            _data_: { id: resolvedId },
          },
          { jwtToken: token, useStoredToken: true }
        );
        const areas = parseParkingAreasResponse(response.json);
        const areaIds = Array.from(new Set(areas.map((area) => area.id).filter((areaId) => areaId.trim().length > 0)));
        setParkingAreaIds((prev) => ({
          ...prev,
          [resolvedId]: areaIds,
        }));
        const footprint = pickFootprintFromAreas(areas);
        if (footprint) {
          setRemoteFootprints((prev) => ({ ...prev, [resolvedId]: footprint }));
        }
        return areaIds;
      } catch (error) {
        console.error('[ParkingDashboard] Nie udało się pobrać poligonu parkingu:', error);
        return cachedAreaIds ?? [];
      } finally {
        fetchingFootprintIdsRef.current.delete(resolvedId);
      }
    },
    [parkingAreaIds, remoteFootprints, token]
  );

  const requestParkingSpotTypes = useCallback(
    async (parkingId: string | number | null | undefined, options: { refresh?: boolean } = {}) => {
      if (!token) {
        return;
      }

      const { refresh = false } = options;
      const resolvedId = normalizeParkingIdentifier(parkingId);
      if (!resolvedId) {
        return;
      }

      const currentSpotTypes = parkingSpotTypes[resolvedId];
      const cachedSpotTypes = readCachedParkingSpotTypes(resolvedId);
      if (Object.keys(cachedSpotTypes).length) {
        updateParkingSpotTypesState(resolvedId, cachedSpotTypes);
      }
      if (!refresh && ((currentSpotTypes && Object.keys(currentSpotTypes).length > 0) || Object.keys(cachedSpotTypes).length > 0)) {
        return;
      }
      if (fetchedSpotTypeIdsRef.current.has(resolvedId)) {
        return;
      }

      const inFlight = fetchingSpotTypeIdsRef.current;
      if (inFlight.has(resolvedId)) {
        return;
      }

      inFlight.add(resolvedId);
      try {
        const areaIds = parkingAreaIds[resolvedId] ?? (await requestParkingFootprint(resolvedId));
        if (!areaIds.length) {
          return;
        }

        for (const areaId of areaIds) {
          const response = await callParkingBoxApi(
            {
              action: 'get-parking-spots',
              _data_: {
                parking_id: resolvedId,
                area_id: areaId,
              },
            },
            { jwtToken: token, useStoredToken: true }
          );

          const parsedSpotTypes = parseParkingSpotTypesResponse(response.json);
          if (!Object.keys(parsedSpotTypes).length) {
            continue;
          }

          const nextSpotTypes = writeCachedParkingSpotTypesArea(resolvedId, areaId, parsedSpotTypes);
          updateParkingSpotTypesState(resolvedId, nextSpotTypes);
        }
        fetchedSpotTypeIdsRef.current.add(resolvedId);
      } catch (error) {
        console.error('[ParkingDashboard] Nie udało się pobrać typów miejsc parkingowych:', error);
      } finally {
        inFlight.delete(resolvedId);
      }
    },
    [parkingAreaIds, parkingSpotTypes, requestParkingFootprint, token, updateParkingSpotTypesState]
  );

  const requestParkingUtilization = useCallback(
    async (parkingId: string | number | null | undefined) => {
      if (!token) {
        return;
      }
      const resolvedId = normalizeParkingIdentifier(parkingId);
      if (!resolvedId) {
        return;
      }
      if (parkingUtilizations[resolvedId]) {
        return;
      }
      const inFlight = fetchingUtilizationIdsRef.current;
      if (inFlight.has(resolvedId)) {
        return;
      }
      inFlight.add(resolvedId);
      setLoadingUtilizationIds((prev) => {
        const next = new Set(prev);
        next.add(resolvedId);
        return next;
      });
      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-utilization',
            _data_: { parking_id: resolvedId },
          },
          { jwtToken: token, useStoredToken: true }
        );
        const chart = normalizeParkingUtilizationResponse(response.json);
        setParkingUtilizations((prev) => ({
          ...prev,
          [resolvedId]: chart.length ? chart : [...DEFAULT_CHART_TEMPLATE],
        }));
      } catch (error) {
        console.error('[ParkingDashboard] Nie udało się pobrać analizy zajętości:', error);
      } finally {
        inFlight.delete(resolvedId);
        setLoadingUtilizationIds((prev) => {
          const next = new Set(prev);
          next.delete(resolvedId);
          return next;
        });
      }
    },
    [parkingUtilizations, token]
  );

  const requestParkingOccupancy = useCallback(
    async (parkingId: string | number | null | undefined) => {
      if (!token) {
        return;
      }

      const resolvedId = normalizeParkingIdentifier(parkingId);
      if (!resolvedId) {
        return;
      }

      const inFlight = fetchingOccupancyIdsRef.current;
      if (inFlight.has(resolvedId)) {
        return;
      }

      inFlight.add(resolvedId);
      setParkingOccupancyResponses((prev) => ({
        ...prev,
        [resolvedId]: {
          status: 'loading',
          summary: prev[resolvedId]?.summary ?? null,
          error: null,
        },
      }));

      try {
        const response = await callParkingBoxApi(
          {
            action: 'get-parking-occupancy',
            _data_: { parking_id: resolvedId },
          },
          { jwtToken: token, useStoredToken: true }
        );
        const payload = response.json ?? response.body;
        const summary = normalizeParkingOccupancyResponse(payload);

        setParkingOccupancyResponses((prev) => ({
          ...prev,
          [resolvedId]: {
            status: 'success',
            summary,
            error: null,
          },
        }));
      } catch (error) {
        const message = error instanceof Error && error.message ? error.message : 'Nie udało się pobrać get-parking-occupancy.';

        console.error('[ParkingDashboard] Nie udało się pobrać get-parking-occupancy:', {
          parkingId: resolvedId,
          error,
        });

        setParkingOccupancyResponses((prev) => ({
          ...prev,
          [resolvedId]: {
            status: 'error',
            summary: prev[resolvedId]?.summary ?? null,
            error: message,
          },
        }));
      } finally {
        inFlight.delete(resolvedId);
      }
    },
    [token]
  );

  const handleSpotFocus = useCallback(
    (spotId: string) => {
      void requestParkingFootprint(spotId);
      void requestParkingSpotTypes(spotId);
      void requestParkingUtilization(spotId);
    },
    [requestParkingFootprint, requestParkingSpotTypes, requestParkingUtilization]
  );

  useEffect(() => {
    if (!token) {
      setRemoteFootprints({});
      setParkingAreaIds({});
      setParkingSpotTypes({});
      fetchingFootprintIdsRef.current.clear();
      fetchingSpotTypeIdsRef.current.clear();
      fetchedSpotTypeIdsRef.current.clear();
      setParkingUtilizations({});
      fetchingUtilizationIdsRef.current.clear();
      setParkingOccupancyResponses({});
      fetchingOccupancyIdsRef.current.clear();
      setLoadingUtilizationIds(new Set());
      prefetchedSpotTypeParkingIdsRef.current.clear();
      setActiveSpotId(null);
    }
  }, [token]);

  const fallbackCities = parkingData.cities;
  const cities: ParkingCity[] = remoteCities.length > 0 ? remoteCities.map(mapCityItemToParkingCity) : fallbackCities;

  const cityMap = useMemo(() => new Map(cities.map((city) => [city.id, city] as const)), [cities]);
  const selectedCity = useMemo(() => {
    if (!selectedCityId) {
      return cities[0] ?? null;
    }
    return cityMap.get(selectedCityId) ?? cities[0] ?? null;
  }, [cityMap, cities, selectedCityId]);

  const filteredParkingListItems = useMemo(() => {
    if (!parkingListItems || parkingListItems.length === 0) {
      return null;
    }
    if (!selectedCityId) {
      return parkingListItems;
    }
    const normalizedSelected = normalizeCityId(selectedCityId);
    const filtered = parkingListItems.filter((item) => {
      const itemCity = normalizeCityId(item.city);
      return normalizedSelected === null || itemCity === null ? true : normalizedSelected === itemCity;
    });
    return filtered.length > 0 ? filtered : parkingListItems;
  }, [parkingListItems, selectedCityId]);

  useEffect(() => {
    if (!filteredParkingListItems || filteredParkingListItems.length === 0) {
      return;
    }

    const parkingIds = filteredParkingListItems
      .map((item) => normalizeParkingIdentifier(item.id))
      .filter((parkingId): parkingId is string => Boolean(parkingId));

    if (!parkingIds.length) {
      return;
    }

    const cachedSpotTypes = readCachedParkingSpotTypesMany(parkingIds);
    if (!Object.keys(cachedSpotTypes).length) {
      return;
    }

    setParkingSpotTypes((prev) => mergeParkingSpotTypeState(prev, cachedSpotTypes));
  }, [filteredParkingListItems]);

  useEffect(() => {
    if (!parkingListFetchedRef.current || isParkingListLoading) {
      return;
    }
    const firstItem = filteredParkingListItems?.[0];
    if (!firstItem || firstItem.id === null || typeof firstItem.id === 'undefined') {
      return;
    }
    void requestParkingFootprint(firstItem.id);
    void requestParkingUtilization(firstItem.id);
  }, [filteredParkingListItems, isParkingListLoading, requestParkingFootprint, requestParkingUtilization]);

  useEffect(() => {
    if (!filteredParkingListItems || filteredParkingListItems.length === 0) {
      return;
    }

    let isCancelled = false;

    const prefetchSpotTypes = async () => {
      for (const item of filteredParkingListItems) {
        if (isCancelled) {
          return;
        }

        const resolvedId = normalizeParkingIdentifier(item.id);
        if (!resolvedId || prefetchedSpotTypeParkingIdsRef.current.has(resolvedId)) {
          continue;
        }

        prefetchedSpotTypeParkingIdsRef.current.add(resolvedId);
        await requestParkingSpotTypes(resolvedId, { refresh: true });
      }
    };

    void prefetchSpotTypes();

    return () => {
      isCancelled = true;
    };
  }, [filteredParkingListItems, requestParkingSpotTypes]);

  useEffect(() => {
    if (!filteredParkingListItems || filteredParkingListItems.length === 0) {
      return;
    }

    filteredParkingListItems.forEach((item) => {
      const resolvedId = normalizeParkingIdentifier(item.id);
      if (!resolvedId) {
        return;
      }

      void requestParkingOccupancy(resolvedId);
    });
  }, [filteredParkingListItems, requestParkingOccupancy]);

  const citySpotsWithChart = useMemo(() => {
    const fallbackSpots = selectedCity?.spots ?? [];
    if (!filteredParkingListItems) {
      return fallbackSpots.map((spot) => {
        const utilization = parkingUtilizations[spot.id];
        return utilization ? { ...spot, chart: utilization } : spot;
      });
    }
    return mapParkingListToSpots(filteredParkingListItems, fallbackSpots, parkingOccupancyResponses).map((spot) => {
      const utilization = parkingUtilizations[spot.id];
      return utilization ? { ...spot, chart: utilization } : spot;
    });
  }, [filteredParkingListItems, parkingOccupancyResponses, parkingUtilizations, selectedCity]);

  const parkingSpots = useMemo(
    () =>
      citySpotsWithChart.map(({ chart, ...spot }) => ({
        ...spot,
        footprint: remoteFootprints[spot.id] ?? spot.footprint,
      })),
    [citySpotsWithChart, remoteFootprints]
  );

  const parkingCharts = useMemo(
    () => Object.fromEntries(citySpotsWithChart.map(({ id, chart }) => [id, chart] as const)) as Record<string, number[]>,
    [citySpotsWithChart]
  );

  const selectedStreet = parkingSpots[0]?.street ?? selectedCity?.highlightStreet ?? '';

  const mapMarkers = useMemo<MapMarker[]>(
    () =>
      parkingSpots.map((spot) => ({
        id: spot.id,
        street: spot.street,
        label: spot.label,
        lngLat: spot.coords,
        variant: spot.street === selectedStreet ? 'highlight' : 'default',
        footprint: spot.footprint,
      })),
    [parkingSpots, selectedStreet]
  );

  const streetMarkerMap = useMemo(() => parkingSpots.reduce<Record<string, string>>((acc, spot) => ({ ...acc, [spot.id]: spot.id }), {}), [parkingSpots]);

  const activeSpot = useMemo(() => {
    if (!parkingSpots.length) {
      return null;
    }
    if (activeSpotId) {
      const match = parkingSpots.find((spot) => spot.id === activeSpotId);
      if (match) {
        return match;
      }
    }
    return parkingSpots[0];
  }, [activeSpotId, parkingSpots]);

  const activeSpotFree = activeSpot ? Math.max(activeSpot.total - activeSpot.occupied, 0) : null;
  const activeSpotFreeHandi = activeSpot?.freeHandi ?? null;
  const activeSpotFreeEv = activeSpot?.freeEv ?? null;
  const activeSpotLabel = activeSpot?.label?.trim().length ? activeSpot.label : activeSpot?.street ?? '';

  const showParkingListLoader = isParkingListLoading && (!parkingListItems || parkingListItems.length === 0);
  const isActiveChartLoading = activeSpotId !== null && loadingUtilizationIds.has(activeSpotId) && !parkingUtilizations[activeSpotId];

  const activeCityId = selectedCity?.id ?? selectedCityId;
  const selectedCityName = selectedCity?.name ?? 'Brak danych';
  const resolvedCityIdForTable = selectedCity?.id ?? selectedCityId;
  const handleMapMarkerSelect = useCallback(
    (markerId: string | null) => {
      const nextSpot = markerId ? parkingSpots.find((spot) => spot.id === markerId) ?? parkingSpots[0] ?? null : parkingSpots[0] ?? null;
      const nextSpotId = nextSpot?.id ?? null;

      setActiveSpotId(nextSpotId);
      if (nextSpotId) {
        handleSpotFocus(nextSpotId);
      }
    },
    [handleSpotFocus, parkingSpots]
  );
  const displayTotals = {
    total: activeSpot ? activeSpot.total.toString() : '--',
    free: activeSpotFree !== null ? activeSpotFree.toString() : '--',
    freeHandi: activeSpotFreeHandi !== null ? activeSpotFreeHandi.toString() : '--',
    freeEv: activeSpotFreeEv !== null ? activeSpotFreeEv.toString() : '--',
  };
  const displayActiveSpotLabel = activeSpotLabel.length ? activeSpotLabel : 'Brak aktywnego parkingu';
  const displayLastUpdate: LastUpdateInfo = lastUpdate ?? {
    dateLabel: '--.--.----',
    timeLabel: '--:--',
  };
  const cityListToggleLabel = isCityListCollapsed ? 'Rozwin liste miast' : 'Zwin liste miast';

  return (
    <main className="min-h-screen bg-slate-200 px-5 py-9 text-slate-900 transition-colors max-lg:px-4 max-lg:py-7 max-md:px-3 max-md:py-6 max-sm:px-3 max-sm:py-5 max-[425px]:px-2 max-[425px]:py-5 dark:bg-neutral-950 dark:text-slate-100">
      <div className="mx-auto w-full max-w-[1920px] overflow-hidden rounded-[32px] bg-white shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] transition-colors max-lg:rounded-[28px] max-md:rounded-[26px] max-sm:rounded-[24px] max-[425px]:rounded-[22px] dark:bg-neutral-900/80 dark:shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]">
        <div
          className={`grid min-h-[760px] grid-cols-1 max-md:min-h-[640px] max-sm:min-h-[auto] max-[425px]:min-h-[auto] ${
            isCityListCollapsed ? 'md:grid-cols-[72px_minmax(0,1fr)]' : 'md:grid-cols-[220px_minmax(0,1fr)]'
          }`}
        >
          <aside
            className={`hidden flex-col border-r border-slate-200 bg-slate-100 text-sm font-semibold uppercase tracking-[0.3em] text-slate-500 transition-colors max-lg:text-xs max-lg:tracking-[0.25em] dark:border-white/10 dark:bg-white/5 dark:text-slate-300 md:flex ${
              isCityListCollapsed ? 'items-center px-3 py-6 max-lg:px-3 max-lg:py-6' : 'px-5 py-9 max-lg:px-4 max-lg:py-7'
            }`}
          >
            <div className={`flex w-full items-center ${isCityListCollapsed ? 'justify-center' : 'justify-between'}`}>
              <span className={`text-xs text-slate-400 dark:text-slate-300/80 ${isCityListCollapsed ? 'sr-only' : ''}`}>Miasta</span>
              <button
                type="button"
                onClick={() => setIsCityListCollapsed((prev) => !prev)}
                className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:border-emerald-500 hover:text-emerald-600 dark:border-white/10 dark:text-slate-300/80 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
                aria-label={cityListToggleLabel}
                aria-expanded={!isCityListCollapsed}
                aria-controls="city-list"
                title={cityListToggleLabel}
              >
                <ChevronIcon className={`h-4 w-4 transition-transform ${isCityListCollapsed ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {!isCityListCollapsed ? (
              <nav id="city-list" className="mt-6 space-y-2">
                {cities.map((city) => {
                  const isActive = city.id === activeCityId;

                  return (
                    <button
                      key={city.id}
                      type="button"
                      onClick={() => setSelectedCityId(city.id)}
                      className={`w-full rounded-2xl px-4 py-2 text-left transition ${
                        isActive
                          ? 'bg-white text-emerald-600 shadow-[0_12px_24px_-16px_rgba(34,197,94,0.8)] dark:bg-emerald-500/10 dark:text-emerald-300 dark:shadow-[0_12px_24px_-16px_rgba(34,197,94,0.45)]'
                          : 'text-slate-400 hover:bg-white/60 hover:text-slate-600 dark:text-slate-400/80 dark:hover:bg-white/10 dark:hover:text-slate-100'
                      }`}
                    >
                      {city.name}
                    </button>
                  );
                })}
              </nav>
            ) : null}
          </aside>

          <div className="flex flex-col">
            <header className="flex flex-col gap-6 border-b border-slate-200 px-5 py-7 transition-colors max-lg:gap-5 max-lg:px-4 max-lg:py-6 max-md:gap-4 max-md:px-3 max-md:py-5 max-sm:gap-3 max-sm:px-2.5 max-sm:py-4 max-[425px]:gap-4 max-[425px]:px-3 max-[425px]:py-5 dark:border-white/10 min-[1280px]:flex-row min-[1280px]:items-center min-[1280px]:justify-between">
              <div className="flex flex-wrap items-center gap-6 max-lg:gap-5 max-md:w-full max-md:gap-4 max-sm:gap-3 max-[425px]:gap-4">
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.4em] max-[1440px]:text-xs max-[1440px]:tracking-[0.3em] max-md:text-xs max-md:tracking-[0.25em] max-sm:text-[11px] max-sm:tracking-[0.2em]">
                  <span className="rounded-lg bg-emerald-500 px-3 py-1 text-white max-md:px-2.5 max-md:py-0.5 max-sm:px-2">Parking</span>
                  <span className="rounded-lg border border-emerald-500 px-3 py-1 text-emerald-600 max-md:px-2.5 max-md:py-0.5 max-sm:px-2 dark:border-emerald-400 dark:text-emerald-300">Box</span>
                </div>
                <h1 className="text-xl font-semibold uppercase tracking-[0.2em] text-slate-700 max-[1440px]:text-lg max-[1440px]:tracking-[0.18em] max-lg:text-lg max-md:text-base max-md:tracking-[0.15em] max-sm:text-sm max-sm:tracking-[0.1em] dark:text-slate-100">
                  Lista parkingów <span className="text-emerald-600 dark:text-emerald-300">{selectedCityName}</span>
                </h1>
              </div>

              <div className="flex flex-wrap items-center gap-6 max-lg:gap-5 max-md:w-full max-md:justify-between max-md:gap-4 max-sm:gap-3 max-[425px]:gap-4">
                <div className="text-right text-xs uppercase tracking-[0.35em] text-slate-400 max-[1440px]:text-[11px] max-[1440px]:tracking-[0.3em] max-md:text-left max-md:text-[10px] max-md:tracking-[0.25em] max-sm:text-[9px] max-sm:tracking-[0.2em] dark:text-slate-300/80">
                  <p>Odczyt danych</p>
                  <p className="mt-1 text-sm tracking-normal text-slate-700 max-[1440px]:text-[0.8rem] max-md:text-xs max-sm:text-[11px] dark:text-slate-100">
                    {displayLastUpdate.dateLabel} <span className="text-slate-400 dark:text-slate-400/70">{displayLastUpdate.timeLabel}</span>
                  </p>
                </div>
                <span className="hidden h-10 w-px bg-slate-200 transition-colors dark:bg-white/10 min-[1280px]:block" />
                <div className="flex items-center gap-4 max-md:hidden">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors max-md:h-10 max-md:w-10 max-sm:h-9 max-sm:w-9 dark:bg-white/10 dark:text-slate-200">
                    <UserIcon className="h-5 w-5 max-md:h-4 max-md:w-4 max-sm:h-4 max-sm:w-4" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.35em] text-slate-400 max-[1440px]:text-[11px] max-[1440px]:tracking-[0.3em] max-md:text-[10px] max-md:tracking-[0.25em] max-sm:text-[9px] max-sm:tracking-[0.2em] dark:text-slate-300/80">
                    <p>Użytkownik</p>
                    <p className="mt-1 max-w-[12rem] truncate text-sm tracking-normal text-slate-700 max-[1440px]:text-[0.8rem] max-md:max-w-[9rem] max-md:text-xs max-sm:max-w-[7.5rem] max-sm:text-[11px] dark:text-slate-100">{displayUserEmail}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div ref={settingsRef} className="relative z-40">
                      <button
                        className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:border-emerald-500 hover:text-emerald-600 max-md:p-1.5 max-sm:p-1 dark:border-white/10 dark:text-slate-300/80 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
                        aria-label="Menu ustawień"
                        type="button"
                        aria-expanded={settingsOpen}
                        onClick={() => setSettingsOpen((prev) => !prev)}
                      >
                        <SettingsIcon className="h-5 w-5 max-md:h-4 max-md:w-4 max-sm:h-4 max-sm:w-4" />
                      </button>
                      {settingsOpen ? (
                        <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl border border-slate-200 bg-white p-2 text-[11px] shadow-lg transition dark:border-white/10 dark:bg-neutral-900">
                          {isAdmin ? (
                            <Link
                              to="/admin/parking/new"
                              className="block rounded-lg px-3 py-2 uppercase tracking-[0.2em] text-slate-600 transition hover:bg-emerald-50 hover:text-emerald-600 dark:text-slate-200 dark:hover:bg-emerald-400/20 dark:hover:text-emerald-300"
                              onClick={() => setSettingsOpen(false)}
                            >
                              Parking
                            </Link>
                          ) : null}
                          <Link
                            to="/admin/vehicles/new"
                            className={`block rounded-lg px-3 py-2 uppercase tracking-[0.2em] text-slate-600 transition hover:bg-emerald-50 hover:text-emerald-600 dark:text-slate-200 dark:hover:bg-emerald-400/20 dark:hover:text-emerald-300 ${isAdmin ? 'mt-1' : ''}`}
                            onClick={() => setSettingsOpen(false)}
                          >
                            Pojazd
                          </Link>
                          {isAdmin ? (
                            <Link
                              to="/admin/cameras"
                              className="mt-1 block rounded-lg px-3 py-2 uppercase tracking-[0.2em] text-slate-600 transition hover:bg-emerald-50 hover:text-emerald-600 dark:text-slate-200 dark:hover:bg-emerald-400/20 dark:hover:text-emerald-300"
                              onClick={() => setSettingsOpen(false)}
                            >
                              Kamery
                            </Link>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="rounded-full border border-slate-200 p-2 text-red-500 transition hover:border-red-500 hover:bg-red-50 hover:text-red-600 max-md:p-1.5 max-sm:p-1 dark:border-white/10 dark:text-red-300 dark:hover:border-red-300 dark:hover:bg-red-500/10 dark:hover:text-red-200"
                      aria-label="Wyloguj"
                      onClick={handleLogout}
                    >
                      <LogoutIcon className="h-5 w-5 max-md:h-4 max-md:w-4 max-sm:h-4 max-sm:w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {!isDesktopLayout ? (
            <section className="flex flex-col gap-5 border-b border-slate-200 px-3 py-5 transition-colors dark:border-white/10">
              {showParkingListLoader ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-[0_8px_30px_-18px_rgba(15,23,42,0.4)] transition-colors dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                  Trwa pobieranie aktualnej listy parkingów...
                </div>
              ) : null}
              {parkingListError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 shadow-[0_8px_30px_-18px_rgba(120,53,15,0.35)] transition-colors dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                  {parkingListError}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-semibold uppercase tracking-[0.35em] text-slate-400 dark:text-slate-300/80">
                <span>Mapa parkingów</span>
                <span className="text-[11px] tracking-[0.2em] text-slate-600 dark:text-slate-200">{selectedCityName}</span>
              </div>

              <div className="relative min-h-[320px] overflow-hidden rounded-[24px] border border-slate-200 transition-colors dark:border-white/10">
                <MapCard
                  markers={mapMarkers}
                  selectedMarkerId={activeSpot?.id ?? null}
                  onMarkerSelect={handleMapMarkerSelect}
                  keepAllPolygonsVisible
                  focusSelectedMarker={false}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/80">
                <span>Wybrany parking</span>
                <span className="text-slate-500 dark:text-slate-300/75">Kliknij obiekt na mapie</span>
              </div>

              <MobileActiveParkingCard spot={activeSpot} cityId={resolvedCityIdForTable} />
            </section>
            ) : null}

            {isDesktopLayout ? (
            <>
            <section className="flex flex-col gap-8 border-b border-slate-200 px-5 py-7 transition-colors max-lg:gap-7 max-lg:px-4 max-lg:py-6 max-md:gap-6 max-md:px-3 max-md:py-5 max-sm:gap-5 max-sm:px-2.5 max-sm:py-4 max-[425px]:gap-4 max-[425px]:px-2.5 max-[425px]:py-3.5 dark:border-white/10 lg:flex-row">
              <div className="flex flex-1 flex-col gap-4">
                {showParkingListLoader ? (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-[0_8px_30px_-18px_rgba(15,23,42,0.4)] transition-colors max-md:px-3 max-md:py-2 max-md:text-xs max-sm:text-[11px] dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                    Trwa pobieranie aktualnej listy parkingów...
                  </div>
                ) : null}
                {parkingListError ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 shadow-[0_8px_30px_-18px_rgba(120,53,15,0.35)] transition-colors max-md:px-3 max-md:py-2 max-md:text-xs max-sm:text-[11px] dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                    {parkingListError}
                  </div>
                ) : null}
                <ParkingTableWithChart
                  cityId={resolvedCityIdForTable}
                  spots={parkingSpots}
                  charts={parkingCharts}
                  selectedStreet={selectedStreet}
                  streetMarkerMap={streetMarkerMap}
                  isLoading={showParkingListLoader}
                  activeChartLoading={isActiveChartLoading}
                  onSpotHover={handleSpotFocus}
                  onSpotSelect={handleSpotFocus}
                  onActiveSpotChange={(spotId, spot) => {
                    setActiveSpotId(spotId);
                    handleSpotFocus(spotId);
                  }}
                />
              </div>
            </section>

            <section className="flex flex-col gap-6 px-5 py-7 max-lg:gap-5 max-lg:px-4 max-lg:py-6 max-md:gap-4 max-md:px-3 max-md:py-5 max-sm:gap-4 max-sm:px-2.5 max-sm:py-4 max-[425px]:gap-5 max-[425px]:px-3 max-[425px]:py-5">
              <div className="flex flex-wrap items-center justify-between text-xs font-semibold uppercase tracking-[0.4em] text-slate-400 max-md:text-[10px] max-md:tracking-[0.25em] max-sm:text-[9px] max-sm:tracking-[0.2em] max-[425px]:gap-3 dark:text-slate-300/80">
                <span>Lokalizacja</span>
                <span className="text-[11px] tracking-[0.2em] text-slate-600 max-md:text-[10px] max-md:tracking-[0.15em] max-sm:text-[9px] max-sm:tracking-[0.12em] dark:text-slate-200">{selectedCityName}</span>
              </div>

              <div className="flex flex-col gap-6 lg:flex-row max-lg:gap-5 max-md:gap-4 max-sm:gap-3 max-[425px]:gap-4">
                <div className="flex aspect-square w-full max-w-[420px] items-center justify-center rounded-full border border-slate-200 bg-white px-9 py-9 text-center shadow-[0_12px_35px_-25px_rgba(15,23,42,0.35)] transition-colors max-lg:mx-auto max-lg:max-w-[380px] max-lg:px-8 max-lg:py-8 max-md:max-w-[340px] max-md:px-7 max-md:py-7 max-sm:max-w-[300px] max-sm:px-6 max-sm:py-6 max-[425px]:max-w-[320px] max-[425px]:rounded-full max-[425px]:px-6 max-[425px]:py-6 dark:border-white/10 dark:bg-white/10 dark:shadow-[0_12px_35px_-20px_rgba(0,0,0,0.6)]">
                  <div className="flex w-full max-w-full flex-col items-center gap-4 text-center">
                    <div className="w-full max-w-[18rem]">
                      <p className="mx-auto mt-1 w-full max-w-[17rem] break-normal px-2 text-center text-sm font-semibold uppercase leading-snug tracking-[0.2em] text-slate-700 whitespace-normal max-md:text-xs max-md:tracking-[0.15em] max-sm:text-[11px] max-sm:tracking-[0.12em] dark:text-slate-100">
                        {displayActiveSpotLabel}
                      </p>
                      {activeSpot?.street && activeSpot.street !== displayActiveSpotLabel ? (
                        <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 max-md:text-[10px] max-md:tracking-[0.18em] max-sm:text-[9px] max-sm:tracking-[0.14em] dark:text-slate-300/70">{activeSpot.street}</p>
                      ) : null}
                    </div>
                    <div className="grid w-full max-w-[18rem] grid-cols-2 gap-x-6 gap-y-4 max-md:gap-x-5 max-md:gap-y-3 max-sm:gap-x-4 max-sm:gap-y-3">
                      <div className="flex flex-col items-center text-center">
                        <p className="flex min-h-[2.6rem] items-center justify-center text-[10px] uppercase leading-snug tracking-[0.5em] text-slate-400 max-md:text-[9px] max-md:tracking-[0.35em] max-sm:min-h-[2.35rem] max-sm:tracking-[0.3em] dark:text-slate-300/70">
                          Miejsca
                        </p>
                        <p className="mt-1 text-[1.75rem] font-semibold text-slate-700 max-md:text-2xl max-sm:text-xl dark:text-slate-100">{displayTotals.total}</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <p className="flex min-h-[2.6rem] items-center justify-center text-[10px] uppercase leading-snug tracking-[0.5em] text-emerald-500 max-md:text-[9px] max-md:tracking-[0.35em] max-sm:min-h-[2.35rem] max-sm:tracking-[0.3em] dark:text-emerald-300">
                          Wolne
                        </p>
                        <p className="mt-1 text-[1.75rem] font-semibold text-emerald-600 max-md:text-2xl max-sm:text-xl dark:text-emerald-300">{displayTotals.free}</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <p className="flex min-h-[2.6rem] items-center justify-center text-[10px] uppercase leading-snug tracking-[0.35em] text-sky-600 max-md:text-[9px] max-md:tracking-[0.28em] max-sm:min-h-[2.35rem] max-sm:tracking-[0.22em] dark:text-sky-300">
                          Wolne niepełn.
                        </p>
                        <p className="mt-1 text-[1.75rem] font-semibold text-slate-700 max-md:text-2xl max-sm:text-xl dark:text-slate-100">{displayTotals.freeHandi}</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <p className="flex min-h-[2.6rem] items-center justify-center text-[10px] uppercase leading-snug tracking-[0.35em] text-amber-600 max-md:text-[9px] max-md:tracking-[0.28em] max-sm:min-h-[2.35rem] max-sm:tracking-[0.22em] dark:text-amber-300">
                          Wolne EV
                        </p>
                        <p className="mt-1 text-[1.75rem] font-semibold text-slate-700 max-md:text-2xl max-sm:text-xl dark:text-slate-100">{displayTotals.freeEv}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative min-h-[340px] flex-1 overflow-hidden rounded-[32px] border border-slate-200 transition-colors max-lg:min-h-[320px] max-md:min-h-[300px] max-md:rounded-[26px] max-sm:min-h-[260px] max-sm:rounded-[22px] max-[425px]:min-h-[260px] max-[425px]:rounded-[20px] dark:border-white/10">
                  <MapCard
                    markers={mapMarkers}
                    selectedMarkerId={activeSpot?.id ?? null}
                    onMarkerSelect={handleMapMarkerSelect}
                    keepAllPolygonsVisible
                  />
                </div>
              </div>
            </section>
            </>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}

const DEFAULT_CHART_TEMPLATE = Array.from({ length: 24 }, () => 0);
const DEFAULT_MARKER_COORDS: [number, number] = [19.1451, 52.2159];

const normalizeCityId = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed.toLowerCase() : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const mapCityItemToParkingCity = (city: ParkingCityItem): ParkingCity => ({
  id: city.id,
  name: city.name,
  highlightStreet: '',
  spots: [],
});

const areSpotTypeMapsEqual = (left: ParkingSpotTypeMap | undefined, right: ParkingSpotTypeMap | undefined) => {
  if (left === right) {
    return true;
  }

  const leftKeys = Object.keys(left ?? {});
  const rightKeys = Object.keys(right ?? {});
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left?.[key] === right?.[key]);
};

const mergeParkingSpotTypeState = (
  previousState: Record<string, ParkingSpotTypeMap>,
  nextEntries: Record<string, ParkingSpotTypeMap>,
) => {
  let hasChanges = false;
  const nextState = { ...previousState };

  Object.entries(nextEntries).forEach(([parkingId, spotTypes]) => {
    if (areSpotTypeMapsEqual(previousState[parkingId], spotTypes)) {
      return;
    }

    nextState[parkingId] = spotTypes;
    hasChanges = true;
  });

  return hasChanges ? nextState : previousState;
};

function normalizeParkingOccupancyResponse(payload: unknown): ParkingOccupancySummary | null {
  const source = extractParkingOccupancySource(payload);
  if (!source) {
    return null;
  }

  let total = 0;
  let occupied = 0;
  let free = 0;
  let hasValues = false;

  Object.values(source).forEach((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return;
    }

    const record = value as Record<string, unknown>;
    const categoryFree = parseOccupancyMetric(record.free);
    const categoryOccupied = parseOccupancyMetric(record.occupied);

    if (categoryFree !== null) {
      free += categoryFree;
      total += categoryFree;
      hasValues = true;
    }

    if (categoryOccupied !== null) {
      occupied += categoryOccupied;
      total += categoryOccupied;
      hasValues = true;
    }
  });

  if (!hasValues) {
    return null;
  }

  return {
    total,
    occupied,
    free,
    freeHandi: readOccupancyFreeCount(source.space_handi ?? source.space_disabled),
    freeEv: readOccupancyFreeCount(source.space_ev),
  };
}

function extractParkingOccupancySource(payload: unknown, depth = 0): Record<string, unknown> | null {
  if (depth > 4 || payload === null || typeof payload === 'undefined') {
    return null;
  }

  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      if (parsed !== payload) {
        return extractParkingOccupancySource(parsed, depth + 1);
      }
    } catch {
      return null;
    }
  }

  if (Array.isArray(payload)) {
    for (const entry of payload) {
      const extracted = extractParkingOccupancySource(entry, depth + 1);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }

  if (typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const hasOccupancyGroups = Object.values(record).some((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }
    const nestedRecord = value as Record<string, unknown>;
    return 'free' in nestedRecord || 'occupied' in nestedRecord;
  });

  if (hasOccupancyGroups) {
    return record;
  }

  const nestedCandidates = [record._data_, record.data, record.response, record.result, record.results, record.payload, record.body];
  for (const candidate of nestedCandidates) {
    if (!candidate || candidate === payload) {
      continue;
    }
    const extracted = extractParkingOccupancySource(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  return null;
}

function readOccupancyFreeCount(value: unknown): number | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return parseOccupancyMetric((value as Record<string, unknown>).free);
}

function parseOccupancyMetric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.round(value));
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized.length) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : null;
  }

  return null;
}

function mapParkingListToSpots(
  items: ParkingListItem[],
  fallbackSpots: ParkingSpotWithChart[],
  parkingOccupancyResponses: Record<string, ParkingOccupancyEntry>,
): ParkingSpotWithChart[] {
  if (items.length === 0) {
    return fallbackSpots;
  }

  return items.map((item, index) => {
    const fallback = pickFallbackSpot(fallbackSpots, index);
    const fallbackStreet = fallback?.street ?? fallback?.label ?? `Parking ${index + 1}`;
    const normalizedStreet = item.address ?? fallbackStreet ?? `Parking ${index + 1}`;
    const normalizedLabel = item.name ?? normalizedStreet;
    const normalizedId = item.id ?? fallback?.id ?? `parking-${index + 1}`;
    const stats = item.stats ?? null;
    const statsTotal =
      stats?.total ?? (stats && stats.occupied !== null && stats.free !== null ? stats.occupied + stats.free : null);
    const statsOccupied =
      stats?.occupied ?? (stats && stats.total !== null && stats.free !== null ? Math.max(stats.total - stats.free, 0) : null);
    const resolvedParkingId = typeof normalizedId === 'number' ? String(normalizedId) : normalizedId;
    const occupancySummary = parkingOccupancyResponses[resolvedParkingId]?.summary ?? null;
    const normalizedSpaces = occupancySummary?.total ?? statsTotal ?? item.space_regular ?? fallback?.total ?? 0;
    const normalizedOccupied = occupancySummary?.occupied ?? statsOccupied ?? fallback?.occupied ?? 0;

    return {
      id: resolvedParkingId,
      street: normalizedStreet,
      label: normalizedLabel,
      total: Math.max(0, Math.round(normalizedSpaces)),
      occupied: Math.max(0, Math.round(normalizedOccupied)),
      freeHandi: occupancySummary?.freeHandi ?? null,
      freeEv: occupancySummary?.freeEv ?? null,
      coords: fallback?.coords ?? DEFAULT_MARKER_COORDS,
      footprint: fallback?.footprint,
      chart: cloneChart(fallback?.chart),
    };
  });
}

function pickFallbackSpot(spots: ParkingSpotWithChart[], index: number): ParkingSpotWithChart | null {
  if (!spots.length) {
    return null;
  }
  return spots[index] ?? spots[index % spots.length] ?? spots[0];
}

function cloneChart(chart?: number[]): number[] {
  if (Array.isArray(chart) && chart.length > 0) {
    return [...chart];
  }
  return [...DEFAULT_CHART_TEMPLATE];
}

function LogoutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M12.9999 2C10.2385 2 7.99991 4.23858 7.99991 7C7.99991 7.55228 8.44762 8 8.99991 8C9.55219 8 9.99991 7.55228 9.99991 7C9.99991 5.34315 11.3431 4 12.9999 4H16.9999C18.6568 4 19.9999 5.34315 19.9999 7V17C19.9999 18.6569 18.6568 20 16.9999 20H12.9999C11.3431 20 9.99991 18.6569 9.99991 17C9.99991 16.4477 9.55219 16 8.99991 16C8.44762 16 7.99991 16.4477 7.99991 17C7.99991 19.7614 10.2385 22 12.9999 22H16.9999C19.7613 22 21.9999 19.7614 21.9999 17V7C21.9999 4.23858 19.7613 2 16.9999 2H12.9999Z" />
      <path d="M13.9999 11C14.5522 11 14.9999 11.4477 14.9999 12C14.9999 12.5523 14.5522 13 13.9999 13V11Z" />
      <path d="M5.71783 11C5.80685 10.8902 5.89214 10.7837 5.97282 10.682C6.21831 10.3723 6.42615 10.1004 6.57291 9.90549C6.64636 9.80795 6.70468 9.72946 6.74495 9.67492L6.79152 9.61162L6.804 9.59454L6.80842 9.58848C6.80846 9.58842 6.80892 9.58778 5.99991 9L6.80842 9.58848C7.13304 9.14167 7.0345 8.51561 6.58769 8.19098C6.14091 7.86637 5.51558 7.9654 5.19094 8.41215L5.18812 8.41602L5.17788 8.43002L5.13612 8.48679C5.09918 8.53682 5.04456 8.61033 4.97516 8.7025C4.83623 8.88702 4.63874 9.14542 4.40567 9.43937C3.93443 10.0337 3.33759 10.7481 2.7928 11.2929L2.08569 12L2.7928 12.7071C3.33759 13.2519 3.93443 13.9663 4.40567 14.5606C4.63874 14.8546 4.83623 15.113 4.97516 15.2975C5.04456 15.3897 5.09918 15.4632 5.13612 15.5132L5.17788 15.57L5.18812 15.584L5.19045 15.5872C5.51509 16.0339 6.14091 16.1336 6.58769 15.809C7.0345 15.4844 7.13355 14.859 6.80892 14.4122L5.99991 15C6.80892 14.4122 6.80897 14.4123 6.80892 14.4122L6.804 14.4055L6.79152 14.3884L6.74495 14.3251C6.70468 14.2705 6.64636 14.1921 6.57291 14.0945C6.42615 13.8996 6.21831 13.6277 5.97282 13.318C5.89214 13.2163 5.80685 13.1098 5.71783 13H13.9999V11H5.71783Z" />
    </svg>
  );
}

function SettingsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 512 512" fill="none" stroke="currentColor" strokeWidth={32} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M262.29,192.31a64,64,0,1,0,57.4,57.4A64.13,64.13,0,0,0,262.29,192.31ZM416.39,256a154.34,154.34,0,0,1-1.53,20.79l45.21,35.46A10.81,10.81,0,0,1,462.52,326l-42.77,74a10.81,10.81,0,0,1-13.14,4.59l-44.9-18.08a16.11,16.11,0,0,0-15.17,1.75A164.48,164.48,0,0,1,325,400.8a15.94,15.94,0,0,0-8.82,12.14l-6.73,47.89A11.08,11.08,0,0,1,298.77,470H213.23a11.11,11.11,0,0,1-10.69-8.87l-6.72-47.82a16.07,16.07,0,0,0-9-12.22,155.3,155.3,0,0,1-21.46-12.57,16,16,0,0,0-15.11-1.71l-44.89,18.07a10.81,10.81,0,0,1-13.14-4.58l-42.77-74a10.8,10.8,0,0,1,2.45-13.75l38.21-30a16.05,16.05,0,0,0,6-14.08c-.36-4.17-.58-8.33-.58-12.5s.21-8.27.58-12.35a16,16,0,0,0-6.07-13.94l-38.19-30A10.81,10.81,0,0,1,49.48,186l42.77-74a10.81,10.81,0,0,1,13.14-4.59l44.9,18.08a16.11,16.11,0,0,0,15.17-1.75A164.48,164.48,0,0,1,187,111.2a15.94,15.94,0,0,0,8.82-12.14l6.73-47.89A11.08,11.08,0,0,1,213.23,42h85.54a11.11,11.11,0,0,1,10.69,8.87l6.72,47.82a16.07,16.07,0,0,0,9,12.22,155.3,155.3,0,0,1,21.46,12.57,16,16,0,0,0,15.11,1.71l44.89-18.07a10.81,10.81,0,0,1,13.14,4.58l42.77,74a10.8,10.8,0,0,1-2.45,13.75l-38.21,30a16.05,16.05,0,0,0-6.05,14.08C416.17,247.67,416.39,251.83,416.39,256Z" />
    </svg>
  );
}

function ChevronIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function UserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.8} stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.125a7.125 7.125 0 0114.25 0V21H4.5v-1.875z" />
    </svg>
  );
}
